import type { UserLocation, Viewport, City, LocationSource } from '../types';

export const STORAGE_KEY = 'parksy_last_location';

export const DEFAULT_VIEWPORT: Viewport = {
  center: [78.9629, 20.5937],
  zoom: 5,
};

export const CITY_ZOOM = 13;
export const SEARCH_RADIUS_METERS = 15000;

// Approximate bounding box for India (generous margins)
const INDIA_BOUNDS = {
  minLat: 6.5,
  maxLat: 37.5,
  minLng: 67.5,
  maxLng: 97.5,
};

export function isWithinIndia(lat: number, lng: number): boolean {
  return (
    lat >= INDIA_BOUNDS.minLat &&
    lat <= INDIA_BOUNDS.maxLat &&
    lng >= INDIA_BOUNDS.minLng &&
    lng <= INDIA_BOUNDS.maxLng
  );
}

export function loadSavedLocation(): UserLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserLocation;
    if (
      typeof parsed.lat === 'number' &&
      typeof parsed.lng === 'number' &&
      isWithinIndia(parsed.lat, parsed.lng)
    ) {
      return { ...parsed, source: 'saved' };
    }
  } catch {
    // ignore corrupt storage
  }
  return null;
}

export function saveLocation(location: Omit<UserLocation, 'savedAt'>): UserLocation {
  const saved: UserLocation = { ...location, savedAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // ignore storage errors (e.g. private mode)
  }
  return saved;
}

export function clearLocation(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function detectGpsLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 600000 }
    );
  });
}

interface IpApiResponse {
  latitude?: number;
  longitude?: number;
  country_name?: string;
  country_code?: string;
}

export async function detectIpLocation(): Promise<{
  lat: number;
  lng: number;
  country: string;
}> {
  const response = await fetch('https://ipapi.co/json/');
  if (!response.ok) {
    throw new Error(`IP geolocation failed: ${response.status}`);
  }
  const data = (await response.json()) as IpApiResponse;
  if (
    typeof data.latitude !== 'number' ||
    typeof data.longitude !== 'number'
  ) {
    throw new Error('IP geolocation returned invalid coordinates.');
  }
  return {
    lat: data.latitude,
    lng: data.longitude,
    country: data.country_name || data.country_code || '',
  };
}

export function getViewportForLocation(
  location: UserLocation | null
): Viewport {
  if (location && isWithinIndia(location.lat, location.lng)) {
    return { center: [location.lng, location.lat], zoom: CITY_ZOOM };
  }
  return DEFAULT_VIEWPORT;
}

export function findNearestMetro(
  lat: number,
  lng: number,
  cities: City[]
): City | null {
  if (cities.length === 0) return null;

  let nearest: City | null = null;
  let minDistance = Infinity;

  for (const city of cities) {
    const distance = haversineKm(lat, lng, city.lat, city.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = city;
    }
  }

  return nearest;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export async function detectCurrentLocation(): Promise<{
  location: UserLocation;
  viewport: Viewport;
  source: LocationSource;
}> {
  // Try GPS first
  try {
    const coords = await detectGpsLocation();
    if (!isWithinIndia(coords.lat, coords.lng)) {
      throw new Error('Detected location is outside India.');
    }
    const location = saveLocation({ ...coords, source: 'gps' });
    return { location, viewport: getViewportForLocation(location), source: 'gps' };
  } catch (gpsError) {
    // Fall back to IP geolocation
    try {
      const ip = await detectIpLocation();
      if (!isWithinIndia(ip.lat, ip.lng)) {
        throw new Error('Detected IP location is outside India.');
      }
      const location = saveLocation({
        lat: ip.lat,
        lng: ip.lng,
        source: 'ip',
      });
      return { location, viewport: getViewportForLocation(location), source: 'ip' };
    } catch (ipError) {
      const cause = ipError instanceof Error ? ipError.message : String(ipError);
      throw new Error(`Location detection failed. ${cause}`);
    }
  }
}
